import { Injectable } from 'angular-ts-decorators';

import { Domain } from '@shared/app/scripts/services/domain';
import type { IConfig } from '@shared/app/scripts/services/config';
import type { IStandardErrorParser } from '@shared/app/scripts/services/standard-error-parser';
import { SpynlBase } from '@shared/app/scripts/services/spynl-base';

/**
 * Factory in the coreModule. It takes care of all user manipulation. Many
 * methods are inherited form {@link coreModule.spynlBase spynlBase}
 * service, and are documented there.
 *
 * # User entity fields
 * <core><pre>
 * {
 *   permissions: array<object>, # array of objects that describe access
 *                               # permissions
 *   tenant_id: array<string>, # array of ids of tenants that are accessible
 *                             # for the user
 *   active: <boolean>, # info is user was activated
 *   fullname: <string>, # user full name
 *   _id: <string>, # id, often equal to email
 *   email: <string>, # user email, often dummy ending with '.nu'
 *   tz: "Europe/Andorra" # time zone name
 * }
 * </pre></core>
 * The object is very similar to the one returned by {@link
 * coreModule.sessionRequests#login login} response. Main differences are
 * in the **tenants** & **tenant_id** array.
 */
@Injectable('user')
export class User extends SpynlBase<Object> {
  protected location: string;

  constructor(
    protected $http: ng.IHttpService,
    protected $q: ng.IQService,
    protected sentry,
    protected domain: Domain,
    protected config: IConfig,
    protected standardErrorParser: IStandardErrorParser,
    private $translate: ng.translate.ITranslateService,
  ) {
    'ngInject';

    super($http, $q, sentry, domain, config, standardErrorParser, Object);

    this.location = '/users';
  }

  /**
   *
   * Try to update the provided keys at the user object
   * @param {string} id
   * _id of the user to be updated
   * @param {object} entityObject
   * Object with the keys that are supposed to be updated.
   * @param {string} tenantId
   * The tenant id of the account. This is an optional parameter. If not provided,
   * it will only change users on the current account.
   * @returns {promise} it will resolve with success data, or fail with error description
   */
  update(id, entityObject, tenantId) {
    let prefix = '',
      url;

    if (tenantId) {
      prefix = '/tenants/' + tenantId;
    }

    url = this.getLocation(prefix) + '/edit';

    if (!angular.isString(id)) {
      // TODO make text translatable
      return this.$q.reject('The entity id is undefined, not able to save it');
    }

    return this.$http
      .post(url, {
        _id: id,
        data: entityObject,
        // TODO CORE-440 move error to catch
      })
      .catch(this.standardErrorParser)
      .then(
        (response) => response.data,
        (error) => this.$q.reject(error.data.message),
      );
  }

  /**
   *
   * Request re-sending email verification for the current user.
   *
   * @returns {promise} it will resolve with true, or fail with error description
   */
  resendEmailVerificationKey() {
    return this.$http
      .get(this.baseUrl() + '/resend-email-verification-key')
      .catch(this.standardErrorParser)
      .then(
        (value) => {
          if (value.data.status === 'error') {
            return this.$q.reject(value.data.message);
          }
          return true;
          // TODO CORE-440 move error to catch
        },
        (error) => this.$q.reject(error.data.message),
      );
  }

  /**
   *
   * Try to request a pass word reset for a specified email address
   *
   * @param {String} username
   * username or email for user that will reset the password
   * @returns {promise} it will resolve with true, or fail with error description
   */
  requestPasswordReset(username) {
    return this.$http
      .get(this.baseUrl() + '/request-pwd-reset', {
        params: { username: username },
      })
      .catch(this.standardErrorParser)
      .then(
        (value) => {
          if (value.data.status === 'error') {
            return this.$q.reject(value.data.message);
          }
          return value.data.message;
          // TODO CORE-440 move error to catch
        },
        (error) => this.$q.reject(error.data.message),
      );
  }

  /**
   *
   * Resets the password for the provided user & key
   *
   * @param {string} username
   * User username or email. Registration email will be sent to the user.
   * @param {string} key
   * Key provided in the email
   * @param {string} password
   * New password for the user
   * @param {string} passwordConfirmation
   * Confirmation on the previously typed password
   * @returns {promise} it will resolve with true, or fail with error description
   */
  passwordReset(username, key, password, passwordConfirmation) {
    return this.$http
      .post(this.baseUrl() + '/reset-pwd', {
        username: username,
        key: key,
        pwd1: password,
        pwd2: passwordConfirmation,
      })
      .catch(this.standardErrorParser)
      .then(
        (value) => {
          if (value.data.status === 'error') {
            return this.$q.reject(value.data.message);
          }

          return true;
          // TODO CORE-440 move error to catch
        },
        (error) => this.$q.reject(error.data.message),
      );
  }

  /**
   *
   * Changes the password for the current user
   *
   * @param {string} currentPassword
   * Current password
   * @param {string} newPassword
   * New password for the user
   * @param {string} newPasswordConfirmation
   * Confirmation on the previously typed password
   * @returns {promise} it will resolve with true, or fail with error description
   */
  changePassword(currentPassword, newPassword, newPasswordConfirmation) {
    return this.$http
      .post(this.baseUrl() + '/change-pwd', {
        current_pwd: currentPassword,
        pwd1: newPassword,
        pwd2: newPasswordConfirmation,
      })
      .catch(this.standardErrorParser)
      .then(
        (value) => {
          if (value.data.status === 'error') {
            return this.$q.reject(value.data.message);
          }

          return true;
          // TODO CORE-440 move error to catch
        },
        (error) => this.$q.reject(error.data.message),
      );
  }

  /**
   *
   * Verfies the given email
   *
   * @param {string} email
   * Email that needs to be verified
   * @param {string} key
   * Key provided in the email
   * @returns {promise} it Will resolve with true, or fail with error description
   */
  verifyEmail(email, key) {
    return this.$http
      .post(this.baseUrl() + '/verify-email', {
        email,
        key,
      })
      .catch(this.standardErrorParser)
      .then(
        (value) => {
          if (value.data.status === 'error') {
            return this.$q.reject(value.data.message);
          }
          return true;
          // TODO CORE-440 move error to catch
        },
        (error) => this.$q.reject(error.data.message),
      );
  }

  /**
   *
   * Change email of current user
   *
   * @param {string} password
   * Password of the current logged in user
   * @param {string} email
   * The new email of the user
   * @returns {promise} It will resolve with true, or fail with error description
   */
  changeOwnEmail(password, email) {
    return this.$http
      .post(this.baseUrl() + '/change-email', {
        current_pwd: password,
        new_email: email,
      })
      .catch(this.standardErrorParser)
      .then(
        (value) => {
          if (value.data.status === 'error') {
            return this.$q.reject(value.data.message);
          }
          return value.data.message;
          // TODO CORE-440 move error to catch
        },
        (error) => this.$q.reject(error.data.message),
      );
  }

  /**
   *
   * Try fetch user by a username filed value.
   *
   * @param {string} username
   * Username
   * @returns {promise} it will resolve with found user, or fail with error description
   */
  getByUsername(username) {
    return this.$http
      .post(this.baseUrl() + '/users/get', {
        filter: { username },
      })
      .then(
        (response: any) => {
          const dataArray = response.data.data;

          switch (dataArray.length) {
            case 0:
              return this.$q.reject('Not found');
            case 1:
              return dataArray[0];
            default:
              this.sentry.captureMessage(
                'Found more then one user entity with username = ' + username,
              );
              return this.$q.reject(
                'Error: found more then 1 user with the same username',
              );
          }
          // TODO CORE-440 move error to catch
        },
        (error) => this.$q.reject(error.data.message),
      );
  }

  /**
   *
   * Try fetch user by a username filed value.
   *
   * @param {object} profileObj
   * The user profile object
   * @returns {promise} it will resolve with true, or fail with error description
   */
  updateUserProfile(profileObj) {
    return (
      this.$http
        .post(this.baseUrl() + '/update-me', { data: profileObj })
        .catch(this.standardErrorParser)
        // TODO CORE-440 move error to catch
        .then(
          (response: any) => response.data.message,
          (error) => this.$q.reject(error.data.message),
        )
    );
  }

  /**
   *
   * Changes the username for the current user
   *
   * @param {string} userId
   * Current user ID
   * @param {string} username
   * The new username
   * @returns {promise} it will resolve with true, or fail with error description
   */
  changeUsername(userId, username) {
    return this.$http
      .post(this.baseUrl() + '/change-username', {
        userId,
        username,
      })
      .catch(this.standardErrorParser)
      .then(
        (value) => {
          if (value.data.status === 'error') {
            return this.$q.reject(value.data.message);
          }

          return true;
          // TODO CORE-440 move error to catch
        },
        (error) => this.$q.reject(error.data.message),
      );
  }

  /**
   *
   * This method can give or remove ownership of an account. An account can be owned
   * by multiple users. The user provided is added to the end of an Array of
   * owners. The owners are stored on the tenant (account) document.
   *
   * @param {string} username
   * The username of the user
   * @param {boolean} isOwner
   * If the user should become an owner of the account, `true` should be passed.
   * If the user should be removed from ownership of the account, then `false`
   * should be passed.
   * @param {string} tenantId
   * The tenant id of the account. This is an optional parameter. If not provided,
   * it will only change ownership on the current account.
   * @returns {promise} it will resolve with true, or fail with error description
   */
  toggleOwner(username, isOwner, tenantId) {
    let tenantSection = '';

    if (!angular.isDefined(username)) {
      return this.$q.reject(
        this.$translate.instant('core.missing_reqired_parameter', {
          param: 'username',
        }),
      );
    }

    if (!angular.isDefined(isOwner)) {
      return this.$q.reject(
        this.$translate.instant('core.missing_reqired_parameter', {
          param: 'isOwner',
        }),
      );
    }

    if (angular.isString(tenantId)) {
      tenantSection = '/tenants/' + tenantId;
    }

    return this.$http
      .post(this.baseUrl() + tenantSection + '/tenants/change-ownership', {
        username,
        is_owner: isOwner,
      })
      .catch(this.standardErrorParser)
      .then(
        (response: any) => response.data.message,
        (error) => this.$q.reject(error.data.message),
      );
  }

  /**
   *
   * This method can activate or deactive a user's account.
   *
   * @param {string} username
   * The username of the user
   * @param {boolean} isActive
   * If the user account should be active, pass `true`, if the user's account
   * should be deactivated pass `false`.
   * @param {string} tenantId
   * The tenant id of the account. This is an optional parameter. If not provided,
   * it will only change users on the current account.
   * @returns {promise} it will resolve with true, or fail with error description
   */
  toggleActive(username, isActive, tenantId) {
    let tenantSection = '';

    if (!angular.isDefined(username)) {
      return this.$q.reject(
        this.$translate.instant('core.missing_reqired_parameter', {
          param: 'username',
        }),
      );
    }

    if (!angular.isDefined(isActive)) {
      return this.$q.reject(
        this.$translate.instant('core.missing_reqired_parameter', {
          param: 'isActive',
        }),
      );
    }

    if (angular.isString(tenantId)) {
      tenantSection = '/tenants/' + tenantId;
    }

    return this.$http
      .post(this.baseUrl() + tenantSection + '/users/change-active', {
        username: username,
        active: isActive,
      })
      .catch(this.standardErrorParser)
      .then(
        (response) => response.data.message,
        (error) => this.$q.reject(error.data.message),
      );
  }
}
